import { Skeleton } from "@/components/ui/skeleton";
import { useProjectMilestones } from "@/hooks/use-milestones";
import { cn } from "@/lib/utils";

interface ScheduleCardProps {
  projectId: string;
  startDate: string | null;
  endDate: string | null;
  scheduleProgress: number;
}

function shortDate(s: string | null | undefined): string {
  if (!s) return "--";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

function deriveStatus(m: {
  status: string | null;
  actualDate: string | null;
  forecastDate: string | null;
  dueDate: string | null;
  contractDate: string | null;
}): "complete" | "active" | "upcoming" {
  if (m.status === "completed" || m.actualDate) return "complete";
  if (m.status === "active" || m.status === "in_progress") return "active";
  return "upcoming";
}

export function ScheduleCard({
  projectId,
  startDate,
  endDate,
  scheduleProgress,
}: ScheduleCardProps) {
  const { data: milestones, isLoading } = useProjectMilestones(projectId);

  const sorted = (milestones ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  let lastCompleteIdx = -1;
  sorted.forEach((m, i) => {
    if (deriveStatus(m) === "complete") lastCompleteIdx = i;
  });
  const startIdx = lastCompleteIdx === -1 ? 0 : lastCompleteIdx;
  const visible = sorted.slice(startIdx, startIdx + 4);

  const total = Math.max(1, visible.length - 1);
  let lastIdx = -1;
  visible.forEach((m, i) => {
    const s = deriveStatus(m);
    if (s === "complete" || s === "active") lastIdx = i;
  });
  const progressPct = lastIdx <= 0 ? 0 : (lastIdx / total) * 100;

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm px-6 py-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-semibold">Schedule</h3>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            {shortDate(startDate)} → {shortDate(endDate)}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">
            Progress
          </div>
          <div className="text-lg font-semibold tabular-nums leading-tight">
            {scheduleProgress}%
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 grid grid-cols-4 gap-2">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-10" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <p className="mt-4 text-[12px] text-muted-foreground">
          No milestones yet for this project.
        </p>
      ) : (
        <div className="relative mt-7 mb-2">
          <div className="absolute left-2 right-2 top-[3px] h-[3px] rounded-full bg-secondary" />
          <div
            className="absolute left-2 top-[3px] h-[3px] rounded-full bg-emerald-500 pbos-bar"
            style={{ width: `calc((100% - 16px) * ${progressPct / 100})` }}
          />
          <ol
            className="relative grid"
            style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}
          >
            {visible.map((m) => {
              const s = deriveStatus(m);
              const dotClass =
                s === "complete"
                  ? "bg-emerald-500 border-emerald-500"
                  : s === "active"
                    ? "bg-blue-500 border-blue-500 pbos-pulse-dot"
                    : "bg-card border-gray-300";
              const date = m.actualDate ?? m.forecastDate ?? m.dueDate ?? m.contractDate;
              return (
                <li key={m.id} className="flex flex-col items-center px-1">
                  <span className={cn("h-[10px] w-[10px] rounded-full border-2", dotClass)} />
                  <div className="mt-2 text-[11px] font-medium text-foreground line-clamp-1 max-w-full text-center">
                    {m.title ?? m.name}
                  </div>
                  <div
                    className={cn(
                      "text-[11px] tabular-nums mt-0.5",
                      s === "active" ? "text-blue-700 font-medium" : "text-muted-foreground",
                    )}
                  >
                    {shortDate(date)}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
